/**
 * Context 包装器组件
 *
 * 这个文件是应用程序的 Context 提供者包装器，负责将所有必要的 Context 提供者
 * 按照正确的层级顺序包装整个应用程序。每个 Context 提供者都有其特定的功能：
 *
 * - TooltipProvider: 提供工具提示功能
 * - ReactFlowProvider: 提供流程图功能
 * - DarkProvider: 提供深色主题功能
 * - TypesProvider: 提供类型和模板管理功能
 * - LocationProvider: 提供位置和导航状态管理
 * - AlertProvider: 提供全局提示和通知功能
 * - SSEProvider: 提供服务器发送事件功能
 * - TabsProvider: 提供标签页和工作流管理功能
 * - UndoRedoProvider: 提供撤销重做功能
 * - UserProvider: 提供用户状态和权限管理
 * - PopUpProvider: 提供弹窗管理功能
 * - ConfigProvider: 提供 Ant Design 组件的中文语言包
 */

import { ConfigProvider } from 'antd'
import zhCN from 'antd/es/locale/zh_CN' // 导入 Ant Design 的中文语言包
import dayjs from 'dayjs'
import 'dayjs/locale/zh-cn'
import { ReactNode } from 'react'
import { ReactFlowProvider } from 'reactflow'
import { TooltipProvider } from '../components/ui/tooltip'
import { SSEProvider } from './SSEContext'
import { AlertProvider } from './alertContext'
import { DarkProvider } from './darkContext'
import { LocationProvider } from './locationContext'
import PopUpProvider from './popUpContext'
import { TabsProvider } from './tabsContext'
import { TypesProvider } from './typesContext'
import { UndoRedoProvider } from './undoRedoContext'
import { UserProvider } from './userContext'

// 设置 dayjs 为中文语言环境
dayjs.locale('zh-cn')

/**
 * Context 包装器组件
 *
 * @param children - 子组件，通常是整个应用程序的根组件
 * @returns 包装了所有 Context 提供者的组件树
 */
export default function ContextWrapper({ children }: { children: ReactNode }) {
  return (
    <>
      {/* 工具提示提供者 - 最外层，为所有组件提供工具提示功能 */}
      <TooltipProvider>
        {/* React Flow 提供者 - 为流程图组件提供上下文 */}
        <ReactFlowProvider>
          {/* 深色主题提供者 - 管理应用程序的深色/浅色主题 */}
          <DarkProvider>
            {/* 类型和模板提供者 - 管理技能组件类型和模板 */}
            <TypesProvider>
              {/* 位置和导航提供者 - 管理当前页面位置和导航状态 */}
              <LocationProvider>
                {/* 提示和通知提供者 - 管理全局错误、成功、通知消息 */}
                <AlertProvider>
                  {/* 服务器发送事件提供者 - 管理实时数据更新 */}
                  <SSEProvider>
                    {/* 标签页和工作流提供者 - 管理工作流和标签页状态 */}
                    <TabsProvider>
                      {/* 撤销重做提供者 - 提供工作流的撤销重做功能 */}
                      <UndoRedoProvider>
                        {/* 用户状态提供者 - 管理用户信息和权限 */}
                        <UserProvider>
                          {/* 弹窗提供者 - 管理全局弹窗状态 */}
                          <PopUpProvider>
                            {/* Ant Design 配置提供者 - 提供中文语言包 */}
                            <ConfigProvider locale={zhCN}>{children}</ConfigProvider>
                          </PopUpProvider>
                        </UserProvider>
                      </UndoRedoProvider>
                    </TabsProvider>
                  </SSEProvider>
                </AlertProvider>
              </LocationProvider>
            </TypesProvider>
          </DarkProvider>
        </ReactFlowProvider>
      </TooltipProvider>
    </>
  )
}
